import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Download, FileSpreadsheet, FileText, Lock, Redo2, RefreshCw, Save, Sheet, Undo2 } from "lucide-react";
import ConfirmDialogue from "../../../../../components/ui/ConfirmDialogue";
import { useWorkspace } from "../../hooks/useEstimateWorkspace";
import useNow from "../../hooks/useNow";
import { Button, IconButton, Pill } from "../../components/ui";
import { EstimateStatusPill } from "../../components/EstimateBadges";
import DropdownMenu from "../../components/DropdownMenu";
import { fmtAgo } from "../../utils/format";

export default function WorkspaceHeader() {
  const ws = useWorkspace();
  const now = useNow(1000);
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const { estimate, draft, config, isLocked, dirty, busy, calculating } = ws;

  const exportItems = [
    { key: "pdf", label: "PDF Bid", hint: "Proposal from the Bid Form tab", icon: FileText, onSelect: () => ws.exportAs("PDF Bid") },
    { key: "xlsx", label: "Excel", hint: "All tabs, workbook format", icon: FileSpreadsheet, onSelect: () => ws.exportAs("Excel") },
    { key: "csv", label: "BOQ CSV", hint: "Bill of quantities only", icon: Sheet, onSelect: () => ws.exportAs("BOQ CSV") },
  ];

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="truncate text-[16px] font-semibold text-text_primary" title={draft.project.projectName}>
            {draft.project.projectName}
          </h1>
          <EstimateStatusPill status={estimate.status} />
          {dirty && !isLocked && (
            <Pill tone="warning" dot>
              Unsaved changes
            </Pill>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-text_muted">
          <span>
            <span className="font-mono text-text_secondary">{estimate.id}</span> · Job #{estimate.jobId} – {estimate.jobTitle}
          </span>
          <Link to="/swiftboard/estimate-settings/versions" className="text-text_secondary hover:underline">
            {config.versionLabel}
          </Link>
          <span aria-live="polite">{calculating ? "Calculating…" : `Last calculated ${fmtAgo(ws.lastCalculatedAt, now)}`}</span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {!isLocked && (
          <>
            <IconButton icon={Undo2} label="Undo (Ctrl+Z)" disabled={!ws.canUndo} onClick={ws.undo} />
            <IconButton icon={Redo2} label="Redo (Ctrl+Shift+Z)" disabled={!ws.canRedo} onClick={ws.redo} />
            <Button icon={Save} disabled={!!busy || !dirty} onClick={ws.saveDraft}>
              {busy === "save" ? "Saving…" : "Save Draft"}
            </Button>
            <Button icon={RefreshCw} disabled={!!busy} onClick={ws.recalculate}>
              {busy === "recalc" ? "Recalculating…" : "Recalculate"}
            </Button>
            <Button variant="accent" icon={Lock} disabled={!!busy || draft.lines.length === 0} onClick={() => setConfirmFinalize(true)}>
              {busy === "finalize" ? "Finalizing…" : "Finalize"}
            </Button>
          </>
        )}
        {isLocked && (
          <Button icon={Copy} disabled={!!busy} onClick={ws.duplicate}>
            Duplicate to revise
          </Button>
        )}
        <DropdownMenu label="Export" icon={Download} variant="primary" items={exportItems} disabled={busy === "export"} />
      </div>

      {confirmFinalize && (
        <ConfirmDialogue
          confirmData={{
            title: "Finalize this estimate?",
            message: "The estimate becomes read-only and is logged in History. To change it later, duplicate it as a new revision.",
            confirmText: "Finalize",
            variant: "primary",
          }}
          handleConfirmClose={(ok) => {
            setConfirmFinalize(false);
            if (ok) ws.finalize();
          }}
        />
      )}
    </div>
  );
}
